import * as React from "react";
import { useState, useCallback, useEffect, useContext } from "react";
import { useTable, usePagination, useSortBy } from "react-table";
import {
  EPL_TABLE_COLUMNS,
  invertedMapLabels,
  patientTotals,
  selectLabels,
} from "../../constants/appConstants";
import SelectionButtons from "../SelectionButtons";
import { AiOutlineDelete } from "react-icons/ai";
import { MultiSelect } from "react-multi-select-component";
import interpolate from "color-interpolate";
import { AuthContext } from "../../context/AuthContext";
import { filterOutLabelsTable } from "../../utils/MapUtils";
import MinMaxSlider from "../MinMaxSlider";
import InputField from "../InputField";
import CustomDropdown from "./../CustomDropdown/index";

let colormap = interpolate(["green", "white", "red"]);

const getMinValue = (data, label) => {
  let minValue = 0;
  data.forEach((item) => {
    if (parseFloat(item[label]) < minValue) {
      minValue = parseFloat(item[label]);
    }
  });
  return minValue;
};

const getMaxValue = (data, label) => {
  let minValue = 0;
  data.forEach((item) => {
    if (parseFloat(item[label]) > minValue) {
      minValue = parseFloat(item[label]);
    }
  });
  return minValue;
};
function normalizeValue(value, minValue, maxValue) {
  if (maxValue === minValue) {
    return 0.5;
  }

  if (value < 0) {
    // Normalize negative values to [0, 0.5]
    return (0.5 * (value - minValue)) / Math.abs(minValue);
  } else {
    // Normalize positive values to [0.5, 1]
    return 0.5 + 0.5 * (value / maxValue);
  }
}

// Function to interpolate color
function interpolateColor(value, minValue, midValue, maxValue) {
  let normalizedValue = normalizeValue(value, minValue, maxValue);
  return colormap(normalizedValue);
}

const Table = ({
  emptyTable,
  InstitutionalVariationTable,
  physicianName,
  setPhysicianName,
  handleSelectRegion = () => {},
  showInputs,
  hcpScatter,
  colorCells,
  updateTable = false,
  isEligible = false,
  setFilterList,
  cellClicked = () => {},
  filterList,
  filterState,
  showTopBtnsToggle = false,
  stateName,
  setStateName,
  cityName,
  setCityName,
  isPayer = false,
  dispatch,
  value,
  setValue,
  handleDelete,
  stateNameList,
  cityNameList,
  organisationList,
  organisation,
  setorganisation,
  cleanedAffilitionList,
  cleanedAffilition,
  setCleanedAffilition,
  cleanedIDNList,
  cleanedIDN,
  setCleanedIDN,
  regionList,
  region,
  setRegion,
  setcurrentSize,
  selectionBtnsArray,
  handleRowClicked,
  currentSize,
  handleFilter,
  sortBy,
  sortOrder,
  handleSort,
  currentPage,
  totalPage,
  marginTop = "0rem",
  activeCells = true,
  Title = "",
  UserTable = false,
  initialState = {
    pageSize: 10,
    pageIndex: 0,
    sortBy: [
      {
        id: "Total_Claims",
        desc: true,
      },
    ],
  },
  setItemId,
  setCurrentPage = () => {},
  showSelectionBtns = true,
  TableData = [],
  TableColummns = EPL_TABLE_COLUMNS,
  speciality,
  setSpeciality,
  specialityList,
  onExportExcel,
}) => {
  const data = React.useMemo(() => TableData, [TableData]);
  const columns = React.useMemo(() => TableColummns, [TableColummns]);
  const {
    getTableProps,
    allColumns,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state: { pageIndex, pageSize },
  } = useTable({ columns, data, initialState }, useSortBy, usePagination);
  const [loading, setLoading] = useState(false);
  // const [openPopup, setOpenPopup] = useState(false);
  // const [barChartConfig, setBarChartConfig] = useState(null);

  const { selectedUnmet } = useContext(AuthContext);

  useEffect(() => {
    if (currentSize) {
      setPageSize(currentSize);
    }
  }, [currentSize, setPageSize]);

  const handleClick = (row) => {
    if (isEligible) {
      handleRowClicked(row);
    }
  };

  const handleFilterClick = (col) => {
    return col.toggleHidden();
  };

  const handleNext = () => {
    if (totalPage) {
      setCurrentPage((prev) => (prev === totalPage ? prev : prev + 1));
    } else {
      nextPage();
    }
  };

  const handlePrev = () => {
    if (totalPage) {
      setCurrentPage((prev) => (prev === 0 ? prev : prev - 1));
    } else {
      previousPage();
    }
  };

  const handleApplyFilters = () => {
    if (isPayer) {
      handleFilter(speciality, region, stateName, organisation, cleanedIDN);
    } else if (speciality !== undefined) {
      handleFilter(speciality, region, stateName, cityName, organisation);
    } else {
      handleFilter(region, stateName, organisation);
    }
  };

  // Get visible columns with both Header (display name) and accessor (data key)
  const getVisibleColumnsWithAccessors = useCallback(() => {
    return allColumns
      .filter(col => col.isVisible && !col.id.startsWith('mapValue'))
      .map(col => ({
        header: col.Header || col.id,
        accessor: col.id || col.accessor
      }));
  }, [allColumns]);

  const handleExportExcel = async () => {
    setLoading(true);
    try {
      const visibleCols = getVisibleColumnsWithAccessors();
      if (onExportExcel) {
        // Pass visible columns with both header and accessor to custom export handler
        await onExportExcel(TableData, visibleCols);
      } else {
        // Default export functionality with visible columns
        exportToExcel(TableData, Title || "export", visibleCols);
      }
    } finally {
      setLoading(false);
    }
  };

  const exportToExcel = (data, filename, visibleColumns = null) => {
    if (!data || data.length === 0) return;
    
    // Use visible columns if provided, otherwise all columns except internal ones
    let columnMappings = visibleColumns;
    if (!columnMappings) {
      columnMappings = Object.keys(data[0])
        .filter(key => !key.startsWith('mapValue') && key !== 'id')
        .map(key => ({ header: key, accessor: key }));
    }
    
    // CSV headers are the display names
    const headers = columnMappings.map(col => col.header);
    
    const csvContent = [
      headers.join(','),
      ...data.map(row => 
        columnMappings.map(col => {
          // Use accessor to get the data, fall back to header if accessor not found
          let cell = row[col.accessor];
          if (cell === null || cell === undefined) {
            // Try using header as fallback
            cell = row[col.header];
          }
          if (cell === null || cell === undefined) return '';
          // Escape commas and quotes
          cell = String(cell);
          if (cell.includes(',') || cell.includes('"') || cell.includes('\n')) {
            cell = `"${cell.replace(/"/g, '""')}"`;
          }
          return cell;
        }).join(',')
      )
    ].join('\n');

    // Create and download the file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${filename.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleRegionSelect = (val) => {
    setRegion(val);
  };

  const handleStateName = (val) => {
    setStateName(val);
  };

  const handleCityName = (val) => {
    if (setCityName) {
      setCityName(val);
    }
  };

  const handleOrganisationSelect = (val) => {
    setorganisation(val);
  };

  const handleSpecialitySelect = (val) => {
    if (setSpeciality) {
      setSpeciality(val);
    }
  };

  const handleToggleSelect = (val) => {
    allColumns.forEach((item) => {
      if (selectionBtnsArray.includes(item.id)) {
        if (item.isVisible) {
          item.toggleHidden();
        }
        val.forEach((_item) => {
          if (_item.value === item.Header) {
            item.toggleHidden();
          }
        });
      }
    });
    setValue(val);
  };

  const getBackgroundColor = (data, row) => {
    if (!data || !data.unmet_need_1) {
      return "transparent";
    }
    let orginalValue1 = row.original[data.unmet_need_1];
    let orginalValue2 = row.original[data.unmet_need_2];

    if (
      orginalValue1 >= parseInt(data.value_1) &&
      orginalValue2 >= parseInt(data.value_2)
    ) {
      return "rgba(255, 0, 0, 0.5)";
    }
    if (orginalValue1 >= parseInt(data.value_1)) {
      return "rgba(224, 176, 255, 0.5)";
    }
    if (orginalValue2 >= parseInt(data.value_2)) {
      return "rgba(75, 0, 130, 0.5)";
    }
    return "transparent";
  };

  useEffect(() => {
    if (showTopBtnsToggle) {
      let valHeaders = [];
      if (value) {
        valHeaders = value.map((item) => item.col.Header);
      }
      let _obj = {};
      selectedUnmet
        .filter((item) => item.value.toLowerCase().includes("percent"))
        .forEach((item) => {
          _obj[item.value] = item;
        });

      let _val = [];
      allColumns
        .filter((item) => selectionBtnsArray.includes(item.id))
        .forEach((item, index) => {
          if (
            !valHeaders.includes(item.Header) &&
            !_obj.hasOwnProperty(item.id) &&
            index !== 2
          ) {
            item.toggleHidden();
          }
          if (_obj.hasOwnProperty(item.id)) {
            _val.push({
              col: item,
              label: selectLabels[item.Header],
              value: item.Header,
            });
          }
          if (index === 2 && !value) {
            _val.push({
              col: item,
              label: selectLabels[item.Header],
              value: item.Header,
            });
          }
        });
      setValue(_val);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFilterValueChange = (min, max, id) => {
    dispatch({
      type: "handleFilterChange",
      payload: {
        min,
        max,
        id,
      },
    });
  };

  const [RegionValue, setRegionValue] = useState({});

  useEffect(() => {
    if (showInputs && data) {
      let _regionValues = {};
      data.forEach((item) => {
        _regionValues[item["State Name"]] = item["Region"];
      });
      setRegionValue(_regionValues);
    }
  }, [showInputs, data]);

  const handleShowFilters = (val) => {
    dispatch({
      type: "handleResetFilterValues",
      payload: {
        val,
      },
    });
    setFilterList(val);
  };

  const handleChangeRegion = (stateName, value) => {
    setRegionValue((prev) => {
      let _prev = { ...prev };
      _prev[stateName] = value;
      return _prev;
    });
  };

  return (
    <div style={{ marginTop }} className="w-full max-w-full">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between w-full gap-3 mb-4">
        {Title && <h2 className="text-lg font-semibold" style={{ color: '#0f172a' }}>{Title}</h2>}
        {isEligible && (
          <div className="flex flex-wrap items-center gap-2">
            <button
              disabled={loading || !TableData || TableData.length === 0}
              onClick={handleExportExcel}
              className="px-4 py-2 text-sm font-medium text-green-700 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Export to Excel
            </button>
            <button
              disabled={loading}
              onClick={handleApplyFilters}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors"
            >
            {loading ? (
              <div className="text-center">
                <div role="status">
                  <svg
                    aria-hidden="true"
                    className="inline w-8 h-8 text-gray-200 animate-spin dark:text-gray-600 fill-blue-600"
                    viewBox="0 0 100 101"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                      fill="currentColor"
                    />
                    <path
                      d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                      fill="currentFill"
                    />
                  </svg>
                  <span className="sr-only">Loading...</span>
                </div>
              </div>
            ) : (
              "Apply Filter"
            )}
          </button>
          </div>
        )}
      </div>
      {!showTopBtnsToggle && !UserTable && showSelectionBtns && (
        <SelectionButtons
          data={
            selectionBtnsArray
              ? allColumns.filter((item) =>
                  selectionBtnsArray.includes(item.id)
                )
              : allColumns
          }
          visibleKey={"isVisible"}
          onClick={handleFilterClick}
        />
      )}
      {isEligible ? (
        <div className="flex flex-col items-start w-full">
          <div className="flex flex-col lg:flex-row lg:items-center items-start gap-4 md:gap-6 w-full">
            <div className="flex flex-col sm:flex-row sm:items-center mt-2 gap-2 sm:gap-4 w-full sm:w-auto">
              <label className="font-semibold text-sm whitespace-nowrap" style={{ color: '#334155' }}>Select Unmet Needs</label>
              <MultiSelect
                labelledBy=""
                ItemRenderer={CustomOptionRenderer}
                options={
                  filterOutLabelsTable(
                    allColumns.filter((item) =>
                      selectionBtnsArray.includes(item.id)
                    ),
                    selectedUnmet,
                    true,
                  )
                    .map(
                      (item) =>
                        isNaN(item) && {
                          col: item,
                          label: selectLabels[item.Header],
                          value: item.Header,
                        }
                    )
                    .filter((item) => typeof item !== "boolean")
                  // .sort(
                  //   (a, b) =>
                  //     selectionBtnsArray.indexOf(a.col.id) -
                  //     selectionBtnsArray.indexOf(b.col.id)
                  // )
                }
                className="w-full sm:w-[20rem] lg:w-[25rem] z-[5]"
                value={value || []}
                onChange={(val) => handleToggleSelect(val)}
              />
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center mt-2 gap-2 sm:gap-4 w-full sm:w-auto">
              <label className="font-semibold text-sm whitespace-nowrap text-slate-700">Select Filters</label>
              <MultiSelect
                labelledBy=""
                ItemRenderer={CustomOptionRenderer}
                options={Object.values(filterState).map((item) => ({
                  label: selectLabels[item.id],
                  value: item.id,
                }))}
                className="w-full sm:w-[20rem] lg:w-[25rem] z-[5]"
                value={filterList || []}
                onChange={(val) => handleShowFilters(val)}
              />
            </div>
          </div>

          <div className="flex flex-col items-start gap-4 md:gap-6 w-full">
            <div className="flex flex-wrap items-center gap-3 md:gap-4">
              {Object.values(filterState)
                .filter((item) => {
                  let _newFilterValue = filterList.map((item) => item.value);
                  return _newFilterValue.includes(item.id);
                })
                .map((item) => {
                  return (
                    <MinMaxSlider
                      handleValueChange={(min, max) =>
                        handleFilterValueChange(min, max, item.id)
                      }
                      minValue={item.min}
                      maxValue={item.max}
                      label={selectLabels[item.id]}
                    />
                  );
                })}
            </div>
            <div className="flex flex-wrap mt-2 items-start gap-3 md:gap-4">
              {regionList && (
                <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                  <label className="font-semibold text-sm whitespace-nowrap" style={{ color: '#334155' }}>Region</label>
                  <MultiSelect
                    labelledBy=""
                    options={regionList
                      .map((item) => ({ label: item, value: item }))
                      .filter(Boolean)}
                    className="w-[8rem] sm:w-[10rem]"
                    value={region || []}
                    onChange={(val) => handleRegionSelect(val)}
                  />
                </div>
              )}
              {organisationList && (
                <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                  <label className="font-semibold text-sm whitespace-nowrap" style={{ color: '#334155' }}>Health System / IDN</label>
                  <MultiSelect
                    labelledBy=""
                    options={organisationList
                      .map((item) => ({ label: item, value: item }))
                      .filter(Boolean)}
                    className="w-[8rem] sm:w-[10rem]"
                    value={organisation || []}
                    onChange={(val) => handleOrganisationSelect(val)}
                  />
                </div>
              )}
              {stateNameList && (
                <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                  <label className="font-semibold text-sm whitespace-nowrap" style={{ color: '#334155' }}>State</label>
                  <MultiSelect
                    labelledBy=""
                    options={stateNameList
                      .map((item) => ({ label: item, value: item }))
                      .filter(Boolean)}
                    className="w-[8rem] sm:w-[10rem]"
                    value={stateName || []}
                    onChange={(val) => handleStateName(val)}
                  />
                </div>
              )}
              {cityNameList && (
                <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                  <label className="font-semibold text-sm whitespace-nowrap" style={{ color: '#334155' }}>City</label>
                  <MultiSelect
                    labelledBy=""
                    options={cityNameList
                      .map((item) => ({ label: item, value: item }))
                      .filter(Boolean)}
                    className="w-[8rem] sm:w-[10rem]"
                    value={cityName || []}
                    onChange={(val) => handleCityName(val)}
                  />
                </div>
              )}
              {specialityList && setSpeciality && (
                <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                  <label className="font-semibold text-sm whitespace-nowrap" style={{ color: '#334155' }}>Specialty</label>
                  <MultiSelect
                    labelledBy=""
                    options={specialityList
                      .map((item) => ({ label: item, value: item }))
                      .filter(Boolean)}
                    className="w-[10rem] sm:w-[12rem]"
                    value={speciality || []}
                    onChange={(val) => handleSpecialitySelect(val)}
                  />
                </div>
              )}
              {setPhysicianName && (
                <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                  <label className="font-semibold text-sm whitespace-nowrap text-slate-700">Physician Name</label>
                  <InputField
                    input={{
                      type: "text",
                      id: "Physician Name",
                      name: "Physician Name",
                      value: physicianName,
                    }}
                    onChange={(e) => setPhysicianName(e.target.value)}
                  />
                </div>
              )}
              {cleanedAffilitionList && (
                <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                  <label className="font-semibold text-sm whitespace-nowrap" style={{ color: '#334155' }}>
                    {isPayer ? "Payer Name" : "Hospital / Clinic"}
                  </label>
                  <MultiSelect
                    labelledBy=""
                    options={cleanedAffilitionList
                      .map((item) => ({ label: item, value: item }))
                      .filter(Boolean)}
                    className="w-[10rem] sm:w-[15rem]"
                    value={cleanedAffilition || []}
                    onChange={(val) => setCleanedAffilition(val)}
                  />
                </div>
              )}
              {isPayer && cleanedIDNList && (
                <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                  <label className="font-semibold text-sm whitespace-nowrap" style={{ color: '#334155' }}>Plan Name</label>
                  <MultiSelect
                    labelledBy=""
                    options={cleanedIDNList
                      .map((item) => ({ label: item, value: item }))
                      .filter(Boolean)}
                    className="w-[10rem] sm:w-[15rem]"
                    value={cleanedIDN || []}
                    onChange={(val) => setCleanedIDN(val)}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div></div>
      )}
      {emptyTable || page.length === 0 ? (
        <div className="h-[20rem] grid place-content-center">
          No Data available
        </div>
      ) : (
        <div className="table-responsive overflow-x-auto mt-4">
        <table className="text-sm w-full" {...getTableProps()}>
          <thead>
            {headerGroups.map((headerGroup, index) => (
              <tr key={index} {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column, index) =>
                  isEligible ? (
                    <th
                      key={index}
                      className="cursor-pointer px-2 py-2 font-semibold text-xs uppercase tracking-wide"
                      style={{ 
                        backgroundColor: '#f1f5f9', 
                        color: '#64748b',
                        borderBottom: '1px solid #e2e8f0',
                        whiteSpace: 'normal',
                        wordWrap: 'break-word',
                        minWidth: '50px',
                        maxWidth: '140px',
                        fontSize: '0.75rem'
                      }}
                      onClick={() => handleSort(column)}
                    >
                      {selectLabels[column.render("Header")]
                        ? selectLabels[column.render("Header")]
                        : column.render("Header")}
                      <span>
                        {sortBy === column.id ||
                        (column.id === "Name" && sortBy === "First Name")
                          ? sortOrder === "desc"
                            ? " 🔽"
                            : " 🔼"
                          : ""}
                      </span>
                    </th>
                  ) : (
                    <th
                      {...column.getHeaderProps(column.getSortByToggleProps())}
                      className="px-2 py-2 font-semibold text-xs uppercase tracking-wide"
                      style={{ 
                        backgroundColor: '#f1f5f9', 
                        color: '#64748b',
                        borderBottom: '1px solid #e2e8f0',
                        whiteSpace: 'normal',
                        wordWrap: 'break-word',
                        minWidth: '50px',
                        maxWidth: '140px',
                        fontSize: '0.75rem'
                      }}
                    >
                      {column.render("Header")}
                      <span>
                        {column.isSorted
                          ? column.isSortedDesc
                            ? " 🔽"
                            : " 🔼"
                          : ""}
                      </span>
                    </th>
                  )
                )}
                {(UserTable || InstitutionalVariationTable || showInputs) && (
                  <th className="px-4 py-3" style={{ backgroundColor: '#f1f5f9', color: '#64748b', borderBottom: '1px solid #e2e8f0' }}></th>
                )}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {page.map((row, index) => {
             
              prepareRow(row);
              let bg = "transparent";
              if (hcpScatter) {
                bg = getBackgroundColor(hcpScatter.data, row);
              }
              return (
                <tr
                  key={index}
                  style={{ backgroundColor: bg }}
                  className="hover:bg-slate-100 relative cursor-pointer text-gray-900"
                  onClick={() =>
                    activeCells ? handleClick(row) : cellClicked(row)
                  }
                  {...row.getRowProps()}
                >
                  {row.cells.map((cell, index) => {
                    let cellValue = cell.render("Cell").props.value;
                    let _header = cell.render("Cell").props.column.Header;
                    let minValue = getMinValue(
                      cell.render("Cell").props.data,
                      invertedMapLabels[_header]
                    );
                    let maxValue = getMaxValue(
                      cell.render("Cell").props.data,
                      invertedMapLabels[_header]
                    );

                    let currentValue = parseFloat(cellValue);
                    let midValue = (maxValue - Math.abs(minValue)) / 2;
                    let background = colorCells
                      ? Object.values(selectLabels).includes(_header)
                        ? interpolateColor(
                            currentValue,
                            minValue,
                            midValue,
                            maxValue
                          )
                        : "transparent"
                      : "transparent";
                    return (
                      <td
                        style={{ 
                          background,
                          color: '#1e293b',
                          borderBottom: '1px solid #f1f5f9',
                          wordWrap: 'break-word'
                        }}
                        className="px-2 py-2 text-sm"
                        key={index}
                        {...cell.getCellProps()}
                      >
                        {cell.render("Cell").props.column.Header ===
                        "Top Priority" ? (
                          <span
                            style={{
                              background: cellValue ? "blue" : "transparent",
                            }}
                            className="w-3 h-3 flex rounded-full"
                          ></span>
                        ) : colorCells &&
                          Object.values(selectLabels).includes(_header) ? (
                          `${(cellValue * 100).toFixed(1)}%`
                        ) : typeof cellValue === "number" ? (
                          cell.render("Cell").props &&
                          cell.render("Cell").props.column.Header &&
                          cell
                            .render("Cell")
                            .props.column.Header.includes("Percent") ? (
                            `${cellValue.toFixed(2)}%`
                          ) : (
                            cellValue.toLocaleString()
                          )
                        ) : (
                          cell.render("Cell")
                        )}
                      </td>
                    );
                  })}
                  {(UserTable || InstitutionalVariationTable) && (
                    <td
                      role="cell"
                      onClick={(e) =>
                        InstitutionalVariationTable
                          ? handleDelete(e, row.values, row)
                          : setItemId(row.values.email)
                      }
                      className="px-4 py-3 w-[1rem] z-[4] hover:scale-[1.2] transition-all ease-in-out duration-300"
                      style={{ color: '#1e293b', borderBottom: '1px solid #f1f5f9' }}
                    >
                      <AiOutlineDelete />
                    </td>
                  )}

                  {showInputs && (
                    <td
                      role="cell"
                      className="px-4 py-3 w-[1rem] z-[4] transition-all ease-in-out duration-300"
                      style={{ color: '#1e293b', borderBottom: '1px solid #f1f5f9' }}
                    >
                      <CustomDropdown
                        value={RegionValue[row.original["State Name"]]}
                        handleSelect={(value) =>
                          handleChangeRegion(row.original["State Name"], value)
                        }
                        input={{
                          options: showInputs.map((item) => ({
                            name: item.Region,
                            value: item.Region,
                          })),
                          id: "input",
                          // label: "Select Region"
                        }}
                      />
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
      )}
      <div className="mt-4 flex items-center justify-between">
        {totalPage > 1 && (
          <div>
            {!totalPage && (
              <button
                className="hover:bg-primary hover:text-slate-50 cursor-pointer p-1"
                onClick={() => gotoPage(0)}
                disabled={!canPreviousPage}
              >
                {"<<"}
              </button>
            )}{" "}
            <button
              className="hover:bg-primary hover:text-slate-50 cursor-pointer p-1"
              onClick={handlePrev}
              disabled={!totalPage && !canPreviousPage}
            >
              {"<"}
            </button>{" "}
            <button
              className="hover:bg-primary hover:text-slate-50 cursor-pointer p-1"
              onClick={handleNext}
              disabled={!totalPage && !canNextPage}
            >
              {">"}
            </button>{" "}
            {!totalPage && (
              <button
                className="hover:bg-primary hover:text-slate-50 cursor-pointer p-1"
                onClick={() =>
                  gotoPage(totalPage ? totalPage - 1 : pageCount - 1)
                }
                disabled={!canNextPage}
              >
                {">>"}
              </button>
            )}{" "}
            <span>
              Page{" "}
              <strong>
                {currentPage ? currentPage : pageIndex + 1} of{" "}
                {totalPage ? totalPage : pageOptions.length}
              </strong>{" "}
            </span>
            <select
              value={currentSize ? currentSize : pageSize}
              onChange={(e) => {
                if (totalPage) {
                  setcurrentSize(Number(e.target.value));
                  setPageSize(Number(e.target.value));
                } else {
                  setPageSize(Number(e.target.value));
                }
              }}
            >
              {[5, 10, 20, 30, 40, 50].map((pageSize) => (
                <option key={pageSize} value={pageSize}>
                  Show {pageSize}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>
    </div>
  );
};

export default Table;

export const CustomOptionRenderer = ({ checked, option, onClick }) => {
  const { selectedUnmet } = useContext(AuthContext);
  const [isPrioritized, setIsPrioritized] = useState({});

  useEffect(() => {
    if (selectedUnmet) {
      let selectedUnmetsObj = {};
      selectedUnmet.forEach((element) => {
        selectedUnmetsObj[element.value] = element;
      });
      setIsPrioritized(selectedUnmetsObj);
    }
  }, [selectedUnmet]);

  return (
    <div
      onClick={onClick}
      className={`flex items-center p-2 cursor-pointer ${
        isPrioritized.hasOwnProperty(option.value)
          ? checked
            ? "bg-[#C0C0B0]"
            : "bg-[#FFFFE0]"
          : checked
          ? "bg-gray-200"
          : ""
      }`}
      style={{
        fontWeight: 600,
        color:
          option.label === "Select All"
            ? "#000"
            : patientTotals.includes(option.label)
            ? "#00008B"
            : "#800000",
      }}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={() => {}}
        className="mr-2"
      />
      {option.label}
    </div>
  );
};
